// src/pages/Profile.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import Cropper from 'react-easy-crop';
import { updateUserProfile, updateUserSettings, getFullUserProfile, clearUserErrorStatus, getPublicSetting, getPracticeLanguages } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import ConfirmDialog from '../components/ConfirmDialog';
import ModernCombobox from '../components/ModernCombobox'; 
import ModernDatePicker from '../components/ModernDatePicker'; 
import CopyButton from '../components/CopyButton'; // ✨ Import CopyButton
import ToastStack from '../components/ToastStack';
import countries from '../list_data/countries_en.json';
import languages from '../list_data/languages.json';
import '../styles/Profile.css';

// ------------------------------------------------------------
// Helpers
const toFormData = (user) => {
  const settings = user?.settings || {};
  return {
    username: user?.username || '',
    practice_language: settings.language || 'en_us',
    gemini_api_key: '', // 【核心修正】永遠返回空字串，不顯示現有 API Key
    birthday: settings.birthday || '',
    country: settings.country || '',
    native_language: settings.native_language || '',
    learning_goal: settings.learning_goal || '',
  };
};

const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((k) => a[k] === b[k]);
};

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const createImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', (error) => reject(error));
  image.src = url;
});

const getOutputType = () => 'image/png';

const getExtensionFromType = () => 'png';

const buildMediaUrl = (path, publicBaseUrl) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return publicBaseUrl ? `${publicBaseUrl}/${path}` : '';
};

const parseBackgroundYIndex = (path) => {
  if (!path) return 0;
  const match = path.match(/background_(\d+)(?:_v\d+)?\.png(?:\?.*)?$/i);
  if (!match) return 0;
  return Number(match[1]) || 0;
};

const getCroppedImageBlob = async (imageSrc, cropPixels, inputType, maxSize = 1080) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not available.');
  }

  const maxDimension = Math.max(cropPixels.width, cropPixels.height);
  const scale = maxDimension > maxSize ? maxSize / maxDimension : 1;
  const targetWidth = Math.round(cropPixels.width * scale);
  const targetHeight = Math.round(cropPixels.height * scale);

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  const outputType = getOutputType(inputType);
  const blob = await new Promise((resolve) => {
    canvas.toBlob((result) => resolve(result), outputType);
  });
  if (!blob) {
    throw new Error('Failed to export cropped image.');
  }

  return {
    blob,
    outputType,
    extension: getExtensionFromType(outputType),
  };
};

const COVER_Y_INDEX_MAX = 1000;
const COVER_PREVIEW_HEIGHT = 180;
const COVER_PREVIEW_MIN_WIDTH = 360;
const COVER_PREVIEW_MAX_WIDTH = 1080;
const COVER_PREVIEW_DEFAULT_WIDTH = 720;

// =================================================================
// ==   自定義 Hooks (Custom Hooks)                               ==
// =================================================================

const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const session = useSession();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async ({ formData, initialData }) => {
      const promises = [];
      if (formData.username !== initialData.username) {
        promises.push(updateUserProfile(userId, { username: formData.username }));
      }

      const settingsUpdates = {};
      const initialSettings = initialData.settings || {};
      if (formData.practice_language !== initialSettings.language) {
        settingsUpdates.language = formData.practice_language;
      }
      // 【核心修正】只有當用戶輸入新的 API Key 時才更新，空白表示保持不變
      if (formData.gemini_api_key && formData.gemini_api_key !== initialSettings.gemini_api_key) {
        settingsUpdates.gemini_api_key = formData.gemini_api_key;
        // 如果用戶更新了 API Key，順便清除錯誤狀態
        promises.push(clearUserErrorStatus(userId));
      }
      if (formData.birthday !== (initialSettings.birthday || '')) {
        settingsUpdates.birthday = formData.birthday || null;
      }
      if (formData.country !== (initialSettings.country || '')) {
        settingsUpdates.country = formData.country || null;
      }
      if (formData.native_language !== (initialSettings.native_language || '')) {
        settingsUpdates.native_language = formData.native_language || null;
      }
      if (formData.learning_goal !== (initialSettings.learning_goal || '')) {
        settingsUpdates.learning_goal = formData.learning_goal || null;
      }

      if (Object.keys(settingsUpdates).length > 0) {
        promises.push(updateUserSettings(userId, settingsUpdates));
      }
      if (promises.length === 0) {
        throw new Error("No changes detected.");
      }
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      queryClient.invalidateQueries({ queryKey: ['practiceStatus'] });
    },
    onError: (error) => {
      console.error("Update failed:", error);
    },
  });
};

// =================================================================
// ==   Profile 組件                                              ==
// =================================================================

export default function Profile() {
  const { t } = useTranslation();
  const session = useSession();
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const cachedUser = queryClient.getQueryData(['user', session?.user?.id]);
  
  // ✨ Persist Tab Selection
  const STORAGE_KEY_PROFILE = 'last_active_tab_profile';

  // Restore tab on mount
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const savedTab = localStorage.getItem(STORAGE_KEY_PROFILE);
      const validSections = ['account', 'security', 'preferences', 'api-keys'];
      if (savedTab && validSections.includes(savedTab)) {
        setSearchParams({ tab: savedTab }, { replace: true });
      }
    }
  }, []); // Run once on mount

  const { data: userData, isLoading: isLoadingUserData } = useQuery({
    queryKey: ['user', session?.user?.id],
    queryFn: () => getFullUserProfile(session?.user?.id),
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    placeholderData: () => queryClient.getQueryData(['user', session?.user?.id]),
  });
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const { data: practiceLanguages = [], isLoading: isLoadingPracticeLanguages } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const { mutate: updateUser, isPending: isUpdating, isSuccess, isError, error, reset } = useUpdateUser();

  const [formData, setFormData] = useState(() => toFormData(cachedUser));
  const [resetMessage, setResetMessage] = useState({ text: '', type: 'info' });
  const validSections = useMemo(() => ['account', 'security', 'preferences', 'api-keys'], []);
  const [activeSection, setActiveSection] = useState(() => {
    const rawSection = searchParams.get('tab');
    return validSections.includes(rawSection) ? rawSection : 'account';
  });

  // Save tab on change, but only if explicitly set in URL to prevent overwriting with default on mount
  useEffect(() => {
    if (searchParams.get('tab')) {
      localStorage.setItem(STORAGE_KEY_PROFILE, activeSection);
    }
  }, [activeSection, searchParams]);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState('');
  const [avatarFileType, setAvatarFileType] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [avatarMessage, setAvatarMessage] = useState({ text: '', type: '' });
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = useState(() => localStorage.getItem('avatarCacheBust') || '');
  const avatarSyncRef = useRef({ avatar: '', metadata: '' });
  const avatarObjectUrlRef = useRef(null);
  const avatarInputRef = useRef(null);
  const avatarImgRef = useRef(null);
  const coverInputRef = useRef(null);
  const coverDraftFileRef = useRef(null);
  const coverDraftObjectUrlRef = useRef(null);
  const coverActiveObjectUrlRef = useRef(null);
  const coverDraftImageRef = useRef(null);
  const coverActiveImageRef = useRef(null);
  const coverActiveSrcRef = useRef('');
  const coverPreviewCanvasRef = useRef(null);
  const coverDisplayCanvasRef = useRef(null);
  const coverDisplayRef = useRef(null);
  const coverIgnoreCloseRef = useRef(false);
  const [isCoverModalOpen, setIsCoverModalOpen] = useState(false);
  const [coverDraftSrc, setCoverDraftSrc] = useState('');
  const [coverActiveSrc, setCoverActiveSrc] = useState('');
  const [coverDraftYIndex, setCoverDraftYIndex] = useState(0);
  const [coverActiveYIndex, setCoverActiveYIndex] = useState(0);
  const [coverPreviewWidth, setCoverPreviewWidth] = useState(COVER_PREVIEW_DEFAULT_WIDTH);
  const [coverDraftSize, setCoverDraftSize] = useState({ width: 0, height: 0 });
  const [coverPreviewMaxOffset, setCoverPreviewMaxOffset] = useState(0);
  const [coverDisplaySize, setCoverDisplaySize] = useState({ width: 0, height: 0 });
  const [coverDragMode, setCoverDragMode] = useState(null);
  const [coverMessage, setCoverMessage] = useState({ text: '', type: '' });
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [coverActiveReadyTick, setCoverActiveReadyTick] = useState(0);
  const coverDragStartRef = useRef({ x: 0, y: 0, width: COVER_PREVIEW_DEFAULT_WIDTH, yIndex: 0 });
  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);
  const metadataAvatar = session?.user?.user_metadata?.avatar_url || '';
  const isExternalUrl = useCallback((value) => /^https?:\/\//i.test(value || ''), []);
  
  const avatarUrl = useMemo(() => {
    const avatarPath = userData?.avatar;

    // Priority 1: Stored avatar
    if (avatarPath) {
      if (isExternalUrl(avatarPath)) return avatarPath;
      if (!publicBaseUrl) return avatarPath;
      const baseUrl = `${publicBaseUrl}/${avatarPath}`;
      return avatarCacheBust ? `${baseUrl}?v=${avatarCacheBust}` : baseUrl;
    }

    // Priority 2: Metadata avatar (e.g. Google)
    if (metadataAvatar) {
      return metadataAvatar;
    }

    // Priority 3: Default Avatar
    return '/Resources/imagine/default_avatar/default_avatar.jpeg';
  }, [avatarCacheBust, publicBaseUrl, userData?.avatar, metadataAvatar, isExternalUrl]);

  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const [isAvatarError, setIsAvatarError] = useState(false);
  
  // ✨ Logic updated: Always show image, no fallback to text
  const showAvatarImage = true; 
  const showAvatarSkeleton = isLoadingUserData || (!isAvatarLoaded && !isAvatarError);

  useEffect(() => {
    if (!session?.user?.id) return;
    const currentAvatar = userData?.avatar || '';
    const currentMeta = metadataAvatar || '';
    if (avatarSyncRef.current.avatar === currentAvatar && avatarSyncRef.current.metadata === currentMeta) {
      return;
    }
    avatarSyncRef.current = { avatar: currentAvatar, metadata: currentMeta };

    const isCustomAvatar = currentAvatar && !isExternalUrl(currentAvatar);
    if (isCustomAvatar) return;

    const syncAvatar = async () => {
      try {
        if (currentMeta) {
          if (currentAvatar !== currentMeta) {
            await updateUserProfile(session.user.id, { avatar: currentMeta });
            queryClient.invalidateQueries({ queryKey: ['user', session.user.id] });
          }
        } else if (currentAvatar && isExternalUrl(currentAvatar)) {
          await updateUserProfile(session.user.id, { avatar: null });
          queryClient.invalidateQueries({ queryKey: ['user', session.user.id] });
        }
      } catch (error) {
        console.error('Avatar sync failed:', error);
      }
    };

    syncAvatar();
  }, [session?.user?.id, userData?.avatar, metadataAvatar, isExternalUrl, queryClient]);

  const getCoverMaxOffset = useCallback((imageSize, targetWidth, targetHeight) => {
    if (!imageSize?.width || !imageSize?.height || !targetWidth || !targetHeight) return 0;
    const scale = targetWidth / imageSize.width;
    const scaledHeight = imageSize.height * scale;
    return Math.max(0, Math.round(scaledHeight - targetHeight));
  }, []);
  const drawCoverCanvas = useCallback((canvas, image, targetWidth, targetHeight, yIndex) => {
    if (!canvas || !image || !targetWidth || !targetHeight) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = targetWidth / image.width;
    const scaledHeight = image.height * scale;
    const maxOffset = Math.max(0, Math.round(scaledHeight - targetHeight));
    const clampedIndex = Math.min(COVER_Y_INDEX_MAX, Math.max(0, yIndex));
    const offset = Math.round((clampedIndex / COVER_Y_INDEX_MAX) * maxOffset);

    ctx.clearRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(image, 0, -offset, targetWidth, scaledHeight);
  }, []);
  const clampCoverPreviewWidth = useCallback((nextWidth) => {
    return Math.min(COVER_PREVIEW_MAX_WIDTH, Math.max(COVER_PREVIEW_MIN_WIDTH, Math.round(nextWidth)));
  }, []);

  // ✨ Theme State
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    return () => {
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current);
        avatarObjectUrlRef.current = null;
      }
      if (coverDraftObjectUrlRef.current) {
        URL.revokeObjectURL(coverDraftObjectUrlRef.current);
        coverDraftObjectUrlRef.current = null;
      }
      if (coverActiveObjectUrlRef.current) {
        URL.revokeObjectURL(coverActiveObjectUrlRef.current);
        coverActiveObjectUrlRef.current = null;
      }
    };
  }, []);

  // 追蹤狀態：最後同步的伺服器值、是否正在更新、是否有本地未儲存更動
  const isUpdatingRef = useRef(false);
  const lastSyncedFormRef = useRef(toFormData(cachedUser));
  const isDirtyRef = useRef(false);

  // Auto-dismiss toast messages
  useEffect(() => {
    if (isSuccess || isError) {
      isUpdatingRef.current = false;
      isDirtyRef.current = false;
      const timer = setTimeout(() => {
        reset();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, isError, reset]);

  useEffect(() => {
    if (resetMessage.text) {
      const timer = setTimeout(() => {
        setResetMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [resetMessage]);

  useEffect(() => {
    const syncCacheBust = (event) => {
      if (event?.type === 'storage' && event.key !== 'avatarCacheBust') return;
      const nextValue = localStorage.getItem('avatarCacheBust') || '';
      setAvatarCacheBust(nextValue);
    };
    window.addEventListener('storage', syncCacheBust);
    window.addEventListener('avatar-cache-bust', syncCacheBust);
    return () => {
      window.removeEventListener('storage', syncCacheBust);
      window.removeEventListener('avatar-cache-bust', syncCacheBust);
    };
  }, []);

  useEffect(() => {
    if (!avatarUrl) {
      setIsAvatarLoaded(false);
      setIsAvatarError(false);
      return;
    }
    setIsAvatarLoaded(false);
    setIsAvatarError(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!avatarUrl || !avatarImgRef.current) return;
    if (avatarImgRef.current.complete && avatarImgRef.current.naturalWidth > 0) {
      setIsAvatarLoaded(true);
    }
  }, [avatarUrl]);

  useEffect(() => {
    if (avatarMessage.text) {
      const timer = setTimeout(() => {
        setAvatarMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [avatarMessage]);

  useEffect(() => {
    if (coverMessage.text) {
      const timer = setTimeout(() => {
        setCoverMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [coverMessage]);

  useEffect(() => {
    let isMounted = true;
    if (!coverDraftSrc) {
      coverDraftImageRef.current = null;
      setCoverDraftSize({ width: 0, height: 0 });
      return () => {};
    }

    createImage(coverDraftSrc)
      .then((image) => {
        if (!isMounted) return;
        coverDraftImageRef.current = image;
        setCoverDraftSize({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
      })
      .catch(() => {
        if (!isMounted) return;
        coverDraftImageRef.current = null;
        setCoverDraftSize({ width: 0, height: 0 });
      });

    return () => {
      isMounted = false;
    };
  }, [coverDraftSrc]);

  useEffect(() => {
    if (!coverActiveSrc) {
      coverActiveImageRef.current = null;
      coverActiveSrcRef.current = '';
      return;
    }
    if (coverActiveSrcRef.current === coverActiveSrc && coverActiveImageRef.current) return;

    let isMounted = true;
    createImage(coverActiveSrc)
      .then((image) => {
        if (!isMounted) return;
        coverActiveImageRef.current = image;
        coverActiveSrcRef.current = coverActiveSrc;
        setCoverActiveReadyTick((value) => value + 1);
      })
      .catch(() => {
        if (!isMounted) return;
        coverActiveImageRef.current = null;
        coverActiveSrcRef.current = '';
      });

    return () => {
      isMounted = false;
    };
  }, [coverActiveSrc]);

  useEffect(() => {
    const backgroundPath = userData?.background || '';
    if (!backgroundPath) {
      setCoverActiveSrc('');
      setCoverActiveYIndex(0);
      return;
    }
    setCoverActiveSrc(buildMediaUrl(backgroundPath, publicBaseUrl));
    setCoverActiveYIndex(parseBackgroundYIndex(backgroundPath));
  }, [publicBaseUrl, userData?.background]);

  useEffect(() => {
    const maxOffset = getCoverMaxOffset(coverDraftSize, coverPreviewWidth, COVER_PREVIEW_HEIGHT);
    setCoverPreviewMaxOffset(maxOffset);
  }, [coverDraftSize, coverPreviewWidth, getCoverMaxOffset]);

  useEffect(() => {
    if (!coverDragMode) return;

    const handlePointerMove = (event) => {
      if (coverDragMode === 'pan') {
        if (!coverPreviewMaxOffset) return;
        const deltaY = coverDragStartRef.current.y - event.clientY;
        const ratio = COVER_Y_INDEX_MAX / coverPreviewMaxOffset;
        const nextYIndex = coverDragStartRef.current.yIndex + deltaY * ratio;
        setCoverDraftYIndex(Math.min(COVER_Y_INDEX_MAX, Math.max(0, Math.round(nextYIndex))));
        return;
      }

      const deltaX = event.clientX - coverDragStartRef.current.x;
      const direction = coverDragMode === 'resize-left' ? -1 : 1;
      const nextWidth = coverDragStartRef.current.width + deltaX * direction;
      setCoverPreviewWidth(clampCoverPreviewWidth(nextWidth));
    };

    const handlePointerUp = () => {
      setCoverDragMode(null);
      setTimeout(() => {
        coverIgnoreCloseRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [coverDragMode, coverPreviewMaxOffset, clampCoverPreviewWidth]);

  useEffect(() => {
    if (!coverPreviewCanvasRef.current || !coverDraftImageRef.current) return;
    drawCoverCanvas(
      coverPreviewCanvasRef.current,
      coverDraftImageRef.current,
      coverPreviewWidth,
      COVER_PREVIEW_HEIGHT,
      coverDraftYIndex
    );
  }, [coverDraftYIndex, coverPreviewWidth, coverDraftSrc, coverDraftSize, drawCoverCanvas]);

  useEffect(() => {
    if (!coverDisplayRef.current) return;
    const updateCoverDisplaySize = () => {
      if (!coverDisplayRef.current) return;
      const rect = coverDisplayRef.current.getBoundingClientRect();
      setCoverDisplaySize({
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setCoverDisplaySize({ width: Math.round(width), height: Math.round(height) });
    });

    updateCoverDisplaySize();
    observer.observe(coverDisplayRef.current);

    const frameId = window.requestAnimationFrame(updateCoverDisplaySize);
    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [isLoadingUserData]);

  useEffect(() => {
    if (!coverDisplayCanvasRef.current || !coverActiveImageRef.current) return;
    const canvas = coverDisplayCanvasRef.current;
    const container = coverDisplayRef.current;
    const resolvedWidth = coverDisplaySize.width || Math.round(container?.clientWidth || canvas.clientWidth || 0);
    const resolvedHeight = coverDisplaySize.height || Math.round(container?.clientHeight || canvas.clientHeight || 0);

    if (!resolvedWidth || !resolvedHeight) return;

    if (canvas.width !== resolvedWidth || canvas.height !== resolvedHeight) {
      canvas.width = resolvedWidth;
      canvas.height = resolvedHeight;
    }

    if (coverDisplaySize.width !== resolvedWidth || coverDisplaySize.height !== resolvedHeight) {
      setCoverDisplaySize({ width: resolvedWidth, height: resolvedHeight });
    }

    drawCoverCanvas(
      canvas,
      coverActiveImageRef.current,
      resolvedWidth,
      resolvedHeight,
      coverActiveYIndex
    );
  }, [coverActiveYIndex, coverActiveSrc, coverDisplaySize, coverActiveReadyTick, drawCoverCanvas]);

  useEffect(() => {
    const backgroundPath = userData?.background || '';
    if (!backgroundPath) return;

    let attempts = 0;
    const maxAttempts = 12;
    const intervalId = window.setInterval(() => {
      const canvas = coverDisplayCanvasRef.current;
      const container = coverDisplayRef.current;
      const image = coverActiveImageRef.current;

      if (!canvas || !container || !image) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          window.clearInterval(intervalId);
        }
        return;
      }

      const width = Math.round(container.clientWidth || canvas.clientWidth || 0);
      const height = Math.round(container.clientHeight || canvas.clientHeight || 0);
      const hasRenderedCover = canvas.width > 1 && canvas.height > 1;

      if (width && height) {
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }

        drawCoverCanvas(
          canvas,
          image,
          width,
          height,
          coverActiveYIndex
        );

        setCoverDisplaySize((prev) => (
          prev.width === width && prev.height === height
            ? prev
            : { width, height }
        ));

        if (hasRenderedCover || attempts >= 1) {
          window.clearInterval(intervalId);
          return;
        }
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [userData?.background, coverActiveSrc, coverActiveReadyTick, coverActiveYIndex, drawCoverCanvas]);

  useEffect(() => {
    const rawSection = searchParams.get('tab');
    const nextSection = validSections.includes(rawSection) ? rawSection : 'account';

    if (nextSection !== activeSection) {
      setActiveSection(nextSection);
      return;
    }

    if (!rawSection || !validSections.includes(rawSection)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', nextSection);
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, activeSection, setSearchParams, validSections]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', section);
    setSearchParams(nextParams);
  };

  const startCoverResize = useCallback((side, event) => {
    event.preventDefault();
    event.stopPropagation();
    coverIgnoreCloseRef.current = true;
    coverDragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: coverPreviewWidth,
      yIndex: coverDraftYIndex,
    };
    setCoverDragMode(side);
  }, [coverPreviewWidth, coverDraftYIndex]);

  const startCoverPan = useCallback((event) => {
    if (!coverDraftSrc || !coverDraftSize.width) return;
    event.preventDefault();
    coverIgnoreCloseRef.current = true;
    coverDragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: coverPreviewWidth,
      yIndex: coverDraftYIndex,
    };
    setCoverDragMode('pan');
  }, [coverDraftSrc, coverDraftSize.width, coverPreviewWidth, coverDraftYIndex]);

  const closeAvatarModal = useCallback(() => {
    setIsAvatarModalOpen(false);
    setAvatarSrc('');
    setAvatarFileType('');
    setCroppedAreaPixels(null);
    setIsAvatarUploading(false);
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  }, []);

  const closeCoverModal = useCallback(() => {
    setIsCoverModalOpen(false);
    setCoverDraftSrc('');
    setCoverDraftYIndex(0);
    setCoverPreviewWidth(COVER_PREVIEW_DEFAULT_WIDTH);
    setCoverDraftSize({ width: 0, height: 0 });
    setCoverPreviewMaxOffset(0);
    setCoverDragMode(null);
    setIsCoverUploading(false);
    coverDraftImageRef.current = null;
    coverDraftFileRef.current = null;
    if (coverDraftObjectUrlRef.current) {
      URL.revokeObjectURL(coverDraftObjectUrlRef.current);
      coverDraftObjectUrlRef.current = null;
    }
    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  }, []);

  const handleCoverOverlayClick = useCallback(() => {
    if (coverIgnoreCloseRef.current || isCoverUploading) return;
    closeCoverModal();
  }, [closeCoverModal, isCoverUploading]);

  // 核心修復：只在「伺服器資料改變且本地未被修改」時同步表單
  useEffect(() => {
    if (!userData) return;

    const nextForm = toFormData(userData);
    const lastSynced = lastSyncedFormRef.current;

    const backendChanged = !shallowEqual(nextForm, lastSynced);
    const userUnchangedLocally = shallowEqual(formData, lastSynced);

    // 若伺服器資料有更新，且本地表單仍等於最後同步值，才同步
    if (backendChanged && userUnchangedLocally) {
      setFormData(nextForm);
      lastSyncedFormRef.current = nextForm;
      isDirtyRef.current = false;
    }
    // 若伺服器資料沒變，或使用者有編輯，不覆寫本地狀態，避免閃爍
  }, [userData, formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    isDirtyRef.current = true;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    avatarObjectUrlRef.current = objectUrl;
    setAvatarSrc(objectUrl);
    setAvatarFileType(file.type);
    setIsAvatarModalOpen(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleCoverFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (coverDraftObjectUrlRef.current) {
      URL.revokeObjectURL(coverDraftObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    coverDraftObjectUrlRef.current = objectUrl;
    coverDraftFileRef.current = file;
    setCoverDraftSrc(objectUrl);
    setCoverDraftYIndex(0);
    setCoverPreviewWidth(COVER_PREVIEW_DEFAULT_WIDTH);
    setCoverDragMode(null);
    setIsCoverModalOpen(true);
  };

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleAvatarSave = async () => {
    if (isAvatarUploading) return;
    if (!avatarSrc || !croppedAreaPixels) {
      closeAvatarModal();
      return;
    }

    try {
      setIsAvatarUploading(true);
      const croppedResult = await getCroppedImageBlob(avatarSrc, croppedAreaPixels, avatarFileType);
      if (!croppedResult?.blob) {
        throw new Error('Failed to prepare image.');
      }

      const fileName = 'avatar.png';
      const croppedFile = new File([croppedResult.blob], fileName, { type: croppedResult.outputType });
      const formData = new FormData();
      formData.append('file', croppedFile);

      const { data, error: uploadError } = await supabaseClient.functions.invoke('handle-profile-images', {
        body: formData,
      });

      if (uploadError) {
        throw new Error(uploadError.message || 'Upload failed.');
      }

      if (!data?.path) {
        throw new Error('Upload succeeded but no path returned.');
      }

      const cacheBust = String(Date.now());
      setAvatarCacheBust(cacheBust);
      localStorage.setItem('avatarCacheBust', cacheBust);
      window.dispatchEvent(new Event('avatar-cache-bust'));
      setAvatarMessage({ text: 'Avatar updated successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['user', session?.user?.id] });
      closeAvatarModal();
    } catch (cropError) {
      console.error('Avatar crop failed:', cropError);
      setAvatarMessage({ text: cropError.message || 'Failed to update avatar.', type: 'error' });
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleCoverSave = async () => {
    if (isCoverUploading) return;
    if (!coverDraftSrc || !coverDraftImageRef.current || !coverDraftFileRef.current) {
      closeCoverModal();
      return;
    }

    try {
      setIsCoverUploading(true);
      const formData = new FormData();
      formData.append('file', coverDraftFileRef.current);
      formData.append('kind', 'background');
      formData.append('yIndex', String(coverDraftYIndex));

      const { data, error: uploadError } = await supabaseClient.functions.invoke('handle-profile-images', {
        body: formData,
      });

      if (uploadError) {
        throw new Error(uploadError.message || 'Upload failed.');
      }

      if (!data?.path) {
        throw new Error('Upload succeeded but no path returned.');
      }

      const nextBackgroundUrl = buildMediaUrl(data.path, publicBaseUrl);
      if (coverActiveObjectUrlRef.current) {
        URL.revokeObjectURL(coverActiveObjectUrlRef.current);
        coverActiveObjectUrlRef.current = null;
      }

      if (coverDraftObjectUrlRef.current) {
        URL.revokeObjectURL(coverDraftObjectUrlRef.current);
        coverDraftObjectUrlRef.current = null;
      }
      coverActiveImageRef.current = coverDraftImageRef.current;
      coverActiveSrcRef.current = '';
      setCoverActiveSrc(nextBackgroundUrl);
      setCoverActiveYIndex(coverDraftYIndex);
      setCoverMessage({ text: 'Cover updated successfully!', type: 'success' });
      // Invalidate all related queries to update other components
      queryClient.invalidateQueries({ queryKey: ['user', session?.user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userProfile', session?.user?.id] }); // For Overall page
      queryClient.invalidateQueries({ queryKey: ['friendships'] }); // For Friends page
      setCoverDraftSrc('');
      closeCoverModal();
    } catch (coverError) {
      console.error('Cover upload failed:', coverError);
      setCoverMessage({ text: coverError.message || 'Failed to update cover.', type: 'error' });
    } finally {
      setIsCoverUploading(false);
    }
  };

  const handleRemoveAvatar = () => {
    if (isAvatarUploading) return;
    setIsRemoveConfirmOpen(true);
  };

  const executeRemoveAvatar = async () => {
    try {
      setIsRemoveConfirmOpen(false);
      setIsAvatarUploading(true);
      const { error: removeError } = await supabaseClient.functions.invoke('handle-profile-images', {
        method: 'POST',
        body: { action: 'delete', kind: 'avatar' },
      });

      if (removeError) {
        throw new Error(removeError.message || 'Remove failed.');
      }

      // Update cache bust to force refresh (even if URL is empty, good practice)
      const cacheBust = String(Date.now());
      setAvatarCacheBust(cacheBust);
      localStorage.setItem('avatarCacheBust', cacheBust);
      window.dispatchEvent(new Event('avatar-cache-bust'));
      
      setAvatarMessage({ text: 'Avatar removed successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['user', session?.user?.id] });
    } catch (err) {
      console.error('Avatar remove failed:', err);
      setAvatarMessage({ text: err.message || 'Failed to remove avatar.', type: 'error' });
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    isUpdatingRef.current = true; // 標記開始更新
    updateUser({ formData, initialData: userData });
  };

  const handlePasswordReset = async () => {
    setResetMessage({ text: 'Sending...', type: 'info' });
    const { error } = await supabaseClient.auth.resetPasswordForEmail(session.user.email, {
        redirectTo: `${window.location.origin}/auth/callback`,
    });
    if (error) {
        setResetMessage({ text: `Error: ${error.message}`, type: 'error' });
    } else {
        setResetMessage({ text: 'Password reset email sent! Please check your inbox.', type: 'success' });
    }
  };

  const message = useMemo(() => {
    if (isSuccess) {
      return { text: 'Profile updated successfully!', type: 'success' };
    }
    if (isError) {
      if (error.message === "No changes detected.") {
        return { text: error.message, type: 'info' };
      }
      return { text: `Update failed: ${error.message}`, type: 'error' };
    }
    return { text: '', type: '' };
  }, [isSuccess, isError, error]);

  const formatHongKongTime = (utcDateString) => {
    if (!utcDateString) return 'N/A';
    return formatInTimeZone(utcDateString, 'Asia/Hong_Kong', 'yyyy-MM-dd HH:mm:ss');
  };

  if (isLoadingUserData || isLoadingPracticeLanguages) {
    return <main className="profile-page-container"><h1 className="page-title">{t('profilePage.title')}</h1><Loader /></main>;
  }

  const hasCoverDraft = Boolean(coverDraftSrc && coverDraftSize.width);
  const isCoverPanning = coverDragMode === 'pan';
  const coverYIndexRatio = COVER_Y_INDEX_MAX === 0 ? 0 : Math.min(1, Math.max(0, coverDraftYIndex / COVER_Y_INDEX_MAX));

  return (
    <main className="profile-page-container">
      <ToastStack items={[message, resetMessage, avatarMessage, coverMessage]} />

      <h1 className="page-title">{t('profilePage.title')}</h1>

      <section className="profile-shell" aria-label={t('profilePage.profileShellLabel', 'Profile shell')}>
        <div className="profile-hero">
          <div
            className="profile-cover"
            ref={coverDisplayRef}
            role="img"
            aria-label={t('profilePage.coverLabel', 'Profile cover')}
          >
            <canvas
              className="profile-cover-canvas"
              ref={coverDisplayCanvasRef}
              width={coverDisplaySize.width || 1}
              height={coverDisplaySize.height || 1}
              aria-hidden="true"
            />
            <div className="profile-cover-actions">
              <input
                id="profile-cover-upload"
                className="profile-file-input"
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverFileChange}
              />
              <label className="profile-ghost-btn" htmlFor="profile-cover-upload">
                {t('profilePage.changeCover', 'Change cover')}
              </label>
            </div>
          </div>

          <div className="profile-hero-content">
            <div className="profile-avatar-block">
              <div className="profile-avatar" aria-hidden="true">
                {showAvatarSkeleton && (
                  <div className={`profile-avatar-skeleton ${isAvatarLoaded ? 'is-hidden' : ''}`} />
                )}
                <img
                  className={`profile-avatar-img ${isAvatarLoaded ? 'is-visible' : ''}`}
                  src={avatarUrl}
                  alt={t('profilePage.avatarAlt', 'User avatar')}
                  ref={avatarImgRef}
                  key={avatarUrl}
                  onLoad={() => setIsAvatarLoaded(true)}
                  onError={(e) => {
                    setIsAvatarError(true);
                    e.target.src = '/Resources/imagine/default_avatar/default_avatar.jpeg';
                  }}
                />
              </div>

              <div className="profile-avatar-actions">
                <input
                  id="profile-avatar-upload"
                  className="profile-file-input"
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarFileChange}
                />
                <label className="profile-ghost-btn" htmlFor="profile-avatar-upload">
                  {t('profilePage.changePhoto', 'Upload photo')}
                </label>
                <button 
                  type="button" 
                  className="profile-text-btn" 
                  onClick={handleRemoveAvatar}
                  disabled={!userData?.avatar || isAvatarUploading}
                >
                  {t('profilePage.removePhoto', 'Remove')}
                </button>
              </div>
            </div>

            <div className="profile-identity">
              <div className="profile-name-row">
                <div className="profile-name">{userData?.username || t('profilePage.noName', 'Unnamed user')}</div>
                <span className="profile-badge">{t('profilePage.member', 'Member')}</span>
              </div>
              <div className="profile-handle">{session?.user?.email || ''}</div>
            </div>
          </div>
        </div>

        <div className="profile-tabs-bar" role="tablist" aria-label={t('profilePage.sections.navLabel', 'Profile sections')}>
          <div className="profile-tabs">
            <button
              type="button"
              role="tab"
              className={`profile-tab ${activeSection === 'account' ? 'active' : ''}`}
              onClick={() => handleSectionChange('account')}
              aria-selected={activeSection === 'account'}
            >
              {t('profilePage.sections.account', 'Account')}
            </button>
            <button
              type="button"
              role="tab"
              className={`profile-tab ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => handleSectionChange('security')}
              aria-selected={activeSection === 'security'}
            >
              {t('profilePage.sections.security', 'Security')}
            </button>
            <button
              type="button"
              role="tab"
              className={`profile-tab ${activeSection === 'preferences' ? 'active' : ''}`}
              onClick={() => handleSectionChange('preferences')}
              aria-selected={activeSection === 'preferences'}
            >
              {t('profilePage.sections.preferences', 'Preferences')}
            </button>
            <button
              type="button"
              role="tab"
              className={`profile-tab ${activeSection === 'api-keys' ? 'active' : ''}`}
              onClick={() => handleSectionChange('api-keys')}
              aria-selected={activeSection === 'api-keys'}
            >
              {t('profilePage.sections.ai', 'API Keys')}
            </button>
          </div>
        </div>

        <form id="profile-form" className="profile-form" onSubmit={handleUpdate}>
          <div className="profile-panel-body">
            {activeSection === 'account' && (
              <>
                <div className="input-group">
                  <label htmlFor="user-name">{t('profilePage.userName')}</label>
                  <input
                    type="text"
                    id="user-name"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Please set your username"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="email">{t('profilePage.emailAddress')}</label>
                  <input type="email" id="email" value={session?.user?.email || ''} disabled />
                </div>

                <div className="input-group">
                  <label htmlFor="created-time">{t('profilePage.accountCreated')}</label>
                  <input type="text" id="created-time" value={formatHongKongTime(session?.user?.created_at)} disabled />
                </div>

                <div className="input-group">
                  <label htmlFor="user-id">{t('profilePage.userId')}</label>
                  <div className="readonly-field-wrapper">
                    <span className="readonly-text">{userData?.id || ''}</span>
                    <CopyButton text={userData?.id || ''} />
                  </div>
                </div>
              </>
            )}

            {activeSection === 'preferences' && (
              <>
                {/* ✨ Theme Toggle */}
                <div className="input-group profile-theme-row">
                  <div className="profile-theme-meta">
                    <label className="profile-theme-label">{t('profilePage.appearance', 'Appearance')}</label>
                    <span className="profile-theme-subtitle">
                      {theme === 'light' ? t('profilePage.lightMode', 'Light Mode') : t('profilePage.darkMode', 'Dark Mode')}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="secondary-btn profile-theme-toggle"
                    aria-label="Toggle Theme"
                  >
                    {theme === 'light' ? (
                      // Sun Icon
                      <svg className="profile-theme-icon profile-theme-icon-light" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                      </svg>
                    ) : (
                      // Moon Icon
                      <svg className="profile-theme-icon profile-theme-icon-dark" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                      </svg>
                    )}
                  </button>
                </div>

                <ModernCombobox
                  label={t('profilePage.practiceLanguage', 'Practice Language')}
                  value={formData.practice_language}
                  onChange={(value) => handleInputChange({ target: { name: 'practice_language', value } })}
                  placeholder={t('profilePage.practiceLanguagePlaceholder', '-- Select practice language --')}
                  options={practiceLanguages}
                />

                <ModernDatePicker
                  label={t('profilePage.birthday', 'Birthday')}
                  value={formData.birthday}
                  onChange={(value) => handleInputChange({ target: { name: 'birthday', value } })}
                  placeholder={t('profilePage.birthdayPlaceholder', 'Select your birthday')}
                />

                <ModernCombobox
                  label={t('profilePage.country', 'Country')}
                  value={formData.country}
                  onChange={(value) => handleInputChange({ target: { name: 'country', value } })}
                  placeholder={t('profilePage.countryPlaceholder', '-- Select your country --')}
                  options={countries.map(c => ({ value: c.code, label: c.name }))}
                />

                <ModernCombobox
                  label={t('profilePage.nativeLanguage', 'Native Language')}
                  value={formData.native_language}
                  onChange={(value) => handleInputChange({ target: { name: 'native_language', value } })}
                  placeholder={t('profilePage.nativeLanguagePlaceholder', '-- Select your native language --')}
                  options={languages.map(l => ({ value: l.code, label: l.name_native }))}
                />

                <ModernCombobox
                  label={t('profilePage.learningGoal', 'Learning Goal')}
                  value={formData.learning_goal}
                  onChange={(value) => handleInputChange({ target: { name: 'learning_goal', value } })}
                  placeholder={t('profilePage.learningGoalPlaceholder', '-- Select a goal --')}
                  options={[
                    { value: 'Career Development', label: t('profilePage.learningGoalOptions.career', 'Career Development') },
                    { value: 'Travel', label: t('profilePage.learningGoalOptions.travel', 'Travel') },
                    { value: 'Academic Study', label: t('profilePage.learningGoalOptions.academic', 'Academic Study') },
                    { value: 'Daily Conversation', label: t('profilePage.learningGoalOptions.conversation', 'Daily Conversation') },
                    { value: 'Hobby', label: t('profilePage.learningGoalOptions.hobby', 'Hobby / Interest') }
                  ]}
                />
              </>
            )}

            {activeSection === 'api-keys' && (
              <>
                <div className="input-group">
                  <label htmlFor="gemini-api-key">Gemini API Key</label>
                  <input
                    type="password"
                    id="gemini-api-key"
                    name="gemini_api_key"
                    value={formData.gemini_api_key}
                    onChange={handleInputChange}
                    placeholder={t('profilePage.apiKeyHelp', 'Leave empty to keep your current API key')}
                    autoComplete="off"
                  />
                </div>
              </>
            )}

            {activeSection === 'security' && (
              <>
                <div className="input-group">
                  <label htmlFor="change-password">{t('profilePage.password', 'Password')}</label>
                  <p className="profile-help-text">
                    {t('profilePage.passwordMessage', 'For security, password changes are handled via email.')}
                  </p>
                  <button
                    type="button"
                    id="change-password-btn"
                    className="secondary-btn"
                    onClick={handlePasswordReset}
                  >
                    {t('profilePage.passwordSend', 'Send Password Reset Email')}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="profile-panel-footer">
            <button id="update-button" className="update-btn" disabled={isUpdating} type="submit">
              {isUpdating ? <Loader /> : <span>{t('profilePage.updateButton')}</span>}
            </button>
          </div>
        </form>
      </section>

      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        title={t('profilePage.removeAvatarTitle', 'Remove Photo')}
        message={t('profilePage.confirmRemoveAvatar', 'Are you sure you want to remove your profile photo?')}
        onConfirm={executeRemoveAvatar}
        onCancel={() => setIsRemoveConfirmOpen(false)}
      />

      {isAvatarModalOpen && (
        <div
          className="profile-avatar-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            if (!isAvatarUploading) closeAvatarModal();
          }}
        >
          <div className="profile-avatar-modal" onClick={(event) => event.stopPropagation()}>
            <div className="profile-avatar-modal-header">
              <div className="profile-avatar-modal-title">
                {t('profilePage.avatarCropTitle', 'Adjust your avatar')}
              </div>
              <button type="button" className="profile-avatar-modal-close" onClick={closeAvatarModal}>
                {t('profilePage.avatarCropClose', 'Close')}
              </button>
            </div>

            <div className="profile-avatar-modal-body">
              <div className="profile-cropper">
                <Cropper
                  image={avatarSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                  containerClassName="profile-cropper-container"
                  cropAreaClassName="profile-crop-area"
                  mediaClassName="profile-crop-media"
                />
              </div>

              <div className="profile-crop-controls">
                <label className="profile-crop-label" htmlFor="profile-crop-zoom">
                  {t('profilePage.avatarZoom', 'Zoom')}
                </label>
                <input
                  id="profile-crop-zoom"
                  className="profile-crop-range"
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  disabled={isAvatarUploading}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </div>
            </div>

            <div className="profile-avatar-modal-footer">
              <button
                type="button"
                className="secondary-btn profile-avatar-modal-btn"
                onClick={closeAvatarModal}
                disabled={isAvatarUploading}
              >
                {t('profilePage.avatarCancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="update-btn profile-avatar-modal-btn"
                onClick={handleAvatarSave}
                disabled={isAvatarUploading}
              >
                {isAvatarUploading ? t('profilePage.avatarSaving', 'Saving...') : t('profilePage.avatarSave', 'Use photo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCoverModalOpen && (
        <div
          className="profile-cover-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCoverOverlayClick}
        >
          <div className="profile-cover-modal" onClick={(event) => event.stopPropagation()}>
            <div className="profile-cover-modal-header">
              <div className="profile-cover-modal-title">
                {t('profilePage.coverCropTitle', 'Adjust your cover')}
              </div>
              <button
                type="button"
                className="profile-cover-modal-close"
                onClick={closeCoverModal}
                disabled={isCoverUploading}
              >
                {t('profilePage.coverCropClose', 'Close')}
              </button>
            </div>

            <div className="profile-cover-modal-body">
              <div className="profile-cover-preview">
                <div className="profile-cover-preview-frame">
                  <button
                    type="button"
                    className="profile-cover-resize-handle left"
                    aria-label={t('profilePage.coverResizeLeft', 'Resize cover preview left')}
                    onPointerDown={(event) => startCoverResize('resize-left', event)}
                    disabled={!hasCoverDraft}
                  />
                  <button
                    type="button"
                    className="profile-cover-resize-handle right"
                    aria-label={t('profilePage.coverResizeRight', 'Resize cover preview right')}
                    onPointerDown={(event) => startCoverResize('resize-right', event)}
                    disabled={!hasCoverDraft}
                  />
                  <div
                    className={`profile-cover-preview-canvas-wrap ${isCoverPanning ? 'is-grabbing' : ''}`}
                    onPointerDown={startCoverPan}
                    role="presentation"
                  >
                    <canvas
                      className="profile-cover-preview-canvas"
                      ref={coverPreviewCanvasRef}
                      width={coverPreviewWidth}
                      height={COVER_PREVIEW_HEIGHT}
                      aria-hidden="true"
                    />
                    <div className="profile-cover-drag-hint">
                      {t('profilePage.coverDragHint', 'Drag image to adjust position')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-cover-controls">
                <div className="profile-cover-helper">
                  {t('profilePage.coverPreviewWidth', 'Preview width')}: {coverPreviewWidth}px
                </div>
              </div>
            </div>

            <div className="profile-cover-modal-footer">
              <button
                type="button"
                className="secondary-btn profile-cover-modal-btn"
                onClick={closeCoverModal}
                disabled={isCoverUploading}
              >
                {t('profilePage.coverCancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="update-btn profile-cover-modal-btn"
                onClick={handleCoverSave}
                disabled={!hasCoverDraft || isCoverUploading}
              >
                {isCoverUploading ? t('profilePage.coverSaving', 'Saving...') : t('profilePage.coverSave', 'Use cover')}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
